import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'

export interface ConfigItem {
  id: string
  category: string
  key: string
  label: string
  color: string | null
  icon: string | null
  description: string | null
  sort_order: number
  is_active: boolean
  is_system: boolean
  created_at: string
  updated_at: string
}

export interface ConfigCategory {
  category: string
  label: string
  items: ConfigItem[]
}

export interface ConfigItemCreate {
  category: string
  key: string
  label: string
  color?: string | null
  icon?: string | null
  description?: string | null
  sort_order?: number
  is_active?: boolean
}

export interface ConfigItemUpdate {
  label?: string
  color?: string | null
  icon?: string | null
  description?: string | null
  sort_order?: number
  is_active?: boolean
}

export function useConfigCategories() {
  return useQuery({
    queryKey: ['config-items'],
    queryFn: async () => {
      const { data } = await api.get<ConfigCategory[]>('/v1/config/items')
      return data
    },
    staleTime: 1000 * 60 * 5,
  })
}

export function useConfigByCategory(category: string) {
  return useQuery({
    queryKey: ['config-items', category],
    queryFn: async () => {
      const { data } = await api.get<ConfigItem[]>(`/v1/config/items/${category}`, { params: { include_inactive: true } })
      return data
    },
    enabled: !!category,
  })
}

export function useCreateConfigItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: ConfigItemCreate) => {
      const { data } = await api.post<ConfigItem>('/v1/config/items', payload)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['config-items'] }),
  })
}

export function useUpdateConfigItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data: payload }: { id: string; data: ConfigItemUpdate }) => {
      const { data } = await api.put<ConfigItem>(`/v1/config/items/${id}`, payload)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['config-items'] }),
  })
}

export function useDeleteConfigItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/v1/config/items/${id}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['config-items'] }),
  })
}

export function useSeedConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/v1/config/seed')
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['config-items'] }),
  })
}
